/**
 * =====================================================================
 * 🔐 AUTH CONTEXT — ULTIMATE SAAS IDENTITY LAYER
 * =====================================================================
 *
 * RESPONSABILITÉ
 * ---------------------------------------------------------------------
 * Ce fichier gère toute l'identité utilisateur dans l'application.
 *
 * Il centralise :
 *
 * 1️⃣ Firebase Authentication (identité sécurisée)
 * 2️⃣ API Profile (Postgres source of truth, Firestore fallback only if API is not configured)
 * 3️⃣ Synchronisation Auth ↔ Profile
 * 4️⃣ Gestion des sessions utilisateur
 * 5️⃣ Conversion comptes anonymes → comptes réels
 *
 * ---------------------------------------------------------------------
 * ARCHITECTURE
 *
 * UI
 * ↓
 * Context
 * ↓
 * Services
 * ↓
 * Firebase
 *
 * =====================================================================
 */

import { watchReturnFromPopup, isPopupCancelError } from "./popupReturnWatcher";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from "react";

import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  FacebookAuthProvider,
  EmailAuthProvider,
  deleteUser as deleteFirebaseUser,
  getRedirectResult,
  linkWithRedirect,
  linkWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signInAnonymously,
  linkWithPopup,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence
} from "firebase/auth";

import { auth, isAuthEmulatorEnabled } from "../firebase/firebase";
import { bootstrapRemoteSession } from "../api/karangoApi";
import { isInAppBrowser as isEmbeddedInAppBrowser } from "../inAppBrowser";
import { retryWithBackoff } from "./utils/retryWithBackoff";
import { authRateLimiter } from "./utils/authRateLimit";
import { readShareAttribution, trackConversionEvent } from "../analytics/conversionAnalytics";
import { sendInitialVerificationEmail } from "./sendInitialVerificationEmail";
import { requestPasswordReset, sendBrandedVerificationEmail } from "./emailVerification.api";
import { resolveProviderAuthMode } from "./authProviderMode";
import { unregisterPushDevice } from "../../features/notifications/pushNotifications.service";

import {
  createUserProfile,
  getUserProfile,
  subscribeUserProfile,
} from "../../features/profile/services/profile.repository";


/* =====================================================================
   CONTEXT CREATION
===================================================================== */

const AuthContext = createContext(null);
const REDIRECT_PENDING_STORAGE_KEY = "karango.auth.redirect.pending";
const FORCE_POPUP_STORAGE_KEY = "karango.auth.forcePopupUntil";
const REDIRECT_PENDING_MAX_AGE_MS = 10 * 60 * 1000;
const FORCE_POPUP_MAX_AGE_MS = 6 * 60 * 60 * 1000;
function readStorageItem(key) {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const sessionValue = window.sessionStorage.getItem(key);
    if (sessionValue !== null) {
      return sessionValue;
    }
  } catch (error) {
    // no-op
  }

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorageItem(key, value) {
  if (typeof window === "undefined") {
    return false;
  }

  let didWrite = false;

  try {
    window.sessionStorage.setItem(key, value);
    didWrite = true;
  } catch (error) {
    // no-op
  }

  try {
    window.localStorage.setItem(key, value);
    didWrite = true;
  } catch (error) {
    // no-op
  }

  return didWrite;
}

function removeStorageItem(key) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // no-op
  }

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // no-op
  }
}

function readJsonStorageValue(key) {
  const rawValue = readStorageItem(key);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    removeStorageItem(key);
    return null;
  }
}

function writeJsonStorageValue(key, value) {
  return writeStorageItem(key, JSON.stringify(value));
}

function markRedirectPending(providerId, operation = "signin") {
  return writeJsonStorageValue(REDIRECT_PENDING_STORAGE_KEY, {
    providerId: String(providerId || "unknown"),
    operation: String(operation || "signin"),
    startedAt: Date.now(),
  });
}

function consumeRedirectPending() {
  const payload = readJsonStorageValue(REDIRECT_PENDING_STORAGE_KEY);
  removeStorageItem(REDIRECT_PENDING_STORAGE_KEY);

  if (!payload || typeof payload.startedAt !== "number") {
    return null;
  }

  if (Date.now() - payload.startedAt > REDIRECT_PENDING_MAX_AGE_MS) {
    return null;
  }

  return payload;
}

function isRedirectStorageFailure(error) {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();

  return (
    code === "auth/missing-or-invalid-nonce" ||
    message.includes("missing initial state") ||
    message.includes("storage-partitioned")
  );
}

function readForcePopupUntilTimestamp() {
  const payload = readJsonStorageValue(FORCE_POPUP_STORAGE_KEY);

  if (!payload || typeof payload.until !== "number") {
    removeStorageItem(FORCE_POPUP_STORAGE_KEY);
    return 0;
  }

  if (Date.now() >= payload.until) {
    removeStorageItem(FORCE_POPUP_STORAGE_KEY);
    return 0;
  }

  return payload.until;
}

function markForcePopupEnabled() {
  const until = Date.now() + FORCE_POPUP_MAX_AGE_MS;

  writeJsonStorageValue(FORCE_POPUP_STORAGE_KEY, { until });

  return until;
}

function resolveProfilePhotoUrl(profile, authUser) {
  return (
    profile?.media?.photos?.[0]?.thumbnailUrl ||
    profile?.media?.photos?.[0]?.url ||
    profile?.photoURL ||
    authUser?.photoURL ||
    null
  );
}

function resolvePrimaryProviderId(authUser) {
  if (!authUser) {
    return "anonymous";
  }

  if (authUser.isAnonymous) {
    return "anonymous";
  }

  return authUser.providerData?.[0]?.providerId || "password";
}

function resolveLinkedProviderIds(authUser) {
  if (!authUser) {
    return [];
  }

  if (authUser.isAnonymous) {
    return ["anonymous"];
  }

  const providerIds = Array.isArray(authUser.providerData)
    ? authUser.providerData
        .map((entry) => String(entry?.providerId || "").trim())
        .filter(Boolean)
    : [];

  if (providerIds.length > 0) {
    return providerIds;
  }

  return authUser.email ? ["password"] : [];
}

function normalizeAuthErrorMessage(error) {
  const code = String(error?.code || "").trim().toLowerCase();

  if (code === "auth/popup-closed-by-user") {
    return "La fenêtre Google a été fermée avant la fin de la connexion.";
  }

  if (code === "auth/popup-blocked") {
    return "Le navigateur a bloqué la fenêtre Google. Autorise les popups puis réessaie.";
  }

  if (code === "auth/cancelled-popup-request") {
    return "Une autre tentative de connexion est déjà en cours.";
  }

  if (code === "auth/unauthorized-domain") {
    return "Ce domaine n'est pas autorisé dans Firebase Auth pour le moment.";
  }

  if (code === "auth/operation-not-allowed") {
    return "La connexion choisie n'est pas activée dans Firebase.";
  }

  if (code === "auth/network-request-failed") {
    return "La connexion réseau a échoué pendant l'authentification.";
  }

  if (code === "auth/too-many-requests") {
    return "Trop de tentatives de connexion. Réessaie dans quelques instants.";
  }

  if (code === "auth/requires-recent-login") {
    return "Reconnecte-toi pour confirmer cette action sensible.";
  }

  if (code === "auth/provider-already-linked") {
    return "Cette méthode de connexion est déjà liée à ton compte.";
  }

  if (code === "auth/no-such-provider") {
    return "Cette méthode de connexion n'est pas disponible sur ton compte.";
  }

  if (code === "auth/credential-already-in-use" || code === "auth/email-already-in-use") {
    return "Cette adresse email est déjà utilisée par un autre compte.";
  }

  if (code === "auth/account-exists-with-different-credential") {
    return "Un compte existe déjà avec cette adresse email via une autre méthode de connexion.";
  }

  if (["auth/invalid-credential", "auth/user-not-found", "auth/wrong-password"].includes(code)) {
    return "E-mail ou mot de passe incorrect.";
  }

  if (code === "auth/weak-password") {
    return "Choisis un mot de passe d'au moins 6 caractères.";
  }

  if (
    code === "auth/missing-or-invalid-nonce" ||
    String(error?.message || "").toLowerCase().includes("missing initial state") ||
    String(error?.message || "").toLowerCase().includes("storage-partitioned")
  ) {
    return "La connexion par redirection n'a pas pu être finalisée. Réessaie avec Facebook ou utilise la connexion par e-mail.";
  }

  if (
    code === "auth/operation-not-supported-in-this-environment" ||
    code === "auth/web-storage-unsupported"
  ) {
    return "Ce navigateur limite cette méthode. Utilise Facebook ou la connexion par e-mail.";
  }

  if (code === "auth/invalid-email") {
    return "L'adresse email saisie n'est pas valide.";
  }

  if (code === "auth/missing-password") {
    return "Le mot de passe est requis pour continuer.";
  }

  if (code === "auth/weak-password") {
    return "Choisis un mot de passe un peu plus solide.";
  }

  if (
    code === "auth/invalid-credential" ||
    code === "auth/user-not-found" ||
    code === "auth/wrong-password"
  ) {
    return "Email ou mot de passe incorrect.";
  }

  if (code === "auth/user-disabled") {
    return "Ce compte a été désactivé. Contacte le support si nécessaire.";
  }

  if (code === "auth/expired-action-code" || code === "auth/invalid-action-code") {
    return "Ce lien a expiré ou est invalide. Demande un nouveau lien.";
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return "La connexion n'a pas pu aboutir. Réessaie dans un instant.";
}

/**
 * Corrige le champ `provider` dans Firestore si il ne correspond plus
 * aux providers réels de l'utilisateur Firebase (ex: après conversion
 * d'un compte anonyme en compte Google/Facebook).
 *
 * Appelé explicitement après chaque linkWithPopup pour garantir
 * la cohérence en un seul passage, sans attendre le useEffect.
 */
async function reconcileProfileProviderIfNeeded(firebaseUser) {
  if (!firebaseUser?.uid || firebaseUser.isAnonymous) return;
  // Les champs d'identite (provider, verification, etc.) sont desormais
  // derives exclusivement du token Firebase cote backend.
  // Cette reconciliation client est volontairement desactivee.
}

function resolvePreferredGoogleAuthMode({ forcePopup = false } = {}) {
  if (typeof window === "undefined") {
    return "popup";
  }

  const userAgent = String(window.navigator?.userAgent || "").toLowerCase();
  const isInAppBrowser = isEmbeddedInAppBrowser(userAgent);

  return resolveProviderAuthMode({
    forcePopup,
    isInAppBrowser,
    userAgent,
  });
}


/* =====================================================================
   AUTH PROVIDER
===================================================================== */

export function AuthProvider({ children }) {

  /* ================================================================
     STATE
  ================================================================ */

  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [claims, setClaims] = useState(null);
  const [authStateVersion, setAuthStateVersion] = useState(0);

  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [forcePopupUntil, setForcePopupUntil] = useState(() =>
    readForcePopupUntilTimestamp()
  );
  const forcePopupMode = forcePopupUntil > Date.now();
  const preferredGoogleAuthMode = useMemo(
    () => resolvePreferredGoogleAuthMode({ forcePopup: forcePopupMode }),
    [forcePopupMode]
  );

  const [authError, setAuthError] = useState(null);
  const [remoteSessionStatus, setRemoteSessionStatus] = useState("idle");
  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);
  const bumpAuthStateVersion = useCallback(() => {
    setAuthStateVersion((currentValue) => currentValue + 1);
  }, []);
  const enablePopupFallbackForCurrentSession = useCallback(() => {
    const nextUntil = markForcePopupEnabled();
    setForcePopupUntil(nextUntil);
  }, []);

  const executeRedirectAuthAction = useCallback(
    async ({ providerId, operation = "signin", run }) => {
      const didPersistRedirectState = markRedirectPending(providerId, operation);

      if (!didPersistRedirectState) {
        enablePopupFallbackForCurrentSession();
        const storageError = new Error("Redirect auth storage unavailable.");
        storageError.code = "auth/web-storage-unsupported";
        throw storageError;
      }

      try {
        await run();
      } catch (error) {
        consumeRedirectPending();
        throw error;
      }
    },
    [enablePopupFallbackForCurrentSession]
  );

  useEffect(() => {
    let isCancelled = false;

    async function configureAuthPersistence() {
      try {
        await setPersistence(auth, browserLocalPersistence);
        return;
      } catch (localPersistenceError) {
        if (isCancelled) {
          return;
        }
      }

      try {
        await setPersistence(auth, browserSessionPersistence);
        return;
      } catch (sessionPersistenceError) {
        if (isCancelled) {
          return;
        }
      }

      try {
        await setPersistence(auth, inMemoryPersistence);
      } catch (inMemoryPersistenceError) {
        if (!isCancelled) {
          console.warn(
            "Auth persistence fallback indisponible:",
            inMemoryPersistenceError
          );
        }
      }
    }

    configureAuthPersistence();

    return () => {
      isCancelled = true;
    };
  }, []);


  /* ================================================================
     BOOTSTRAP PROFILE
  ================================================================ */

  /**
   * S'assure que le profil utilisateur existe via l'API Karango.
   * Firestore ne sert que de fallback si l'API n'est pas configuree.
   */

  const bootstrapUserProfile = async (firebaseUser) => {

    try {

      await createUserProfile(firebaseUser);

    } catch (error) {

      console.error("Erreur création profil:", error);
      setAuthError("Ton profil n a pas pu etre initialise. Reessaie de te connecter.");

    }

  };


  /* ================================================================
     LOAD PROFILE
  ================================================================ */

  /**
   * Charge les donnees profil via l'API Karango, avec fallback Firestore si besoin.
   */

  /* ================================================================
     REFRESH PROFILE
  ================================================================ */

  /**
   * Recharge le profil utilisateur via l'API Karango, avec fallback Firestore si besoin.
   * Utile après modification du profil.
   */

  const refreshProfile = useCallback(async () => {

    if (!authUser) return;

    try {

      await createUserProfile(authUser);

      const profileData = await getUserProfile(authUser.uid);

      setProfile(profileData);

      return { success: Boolean(profileData), profile: profileData };

    } catch (error) {

      console.error("Erreur refresh profil:", error);
      setAuthError("Le profil n a pas pu etre recharge. Reessaie dans un instant.");

      return { success: false, error };

    }

  }, [authUser]);


  /* ================================================================
     AUTH LISTENER
  ================================================================ */

  /**
   * Listener Firebase déclenché lorsque :
   * - login
   * - logout
   * - refresh page
   */

  useEffect(() => {

    let isCancelled = false;
    let profileUnsubscribe = () => {};
    let authSyncVersion = 0;
    let loadingFallbackTimer = setTimeout(() => {
      if (!isCancelled) {
        setLoadingGlobal(false);
      }
    }, 10000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {

      const syncVersion = ++authSyncVersion;

      profileUnsubscribe();
      profileUnsubscribe = () => {};

      try {

        if (firebaseUser) {

          setLoadingGlobal(true);
          setProfile(null);

          await bootstrapUserProfile(firebaseUser);

          if (isCancelled || syncVersion !== authSyncVersion) {
            return;
          }

          setAuthUser(firebaseUser);

          profileUnsubscribe = subscribeUserProfile(
            firebaseUser.uid,
            (profileData) => {

              if (isCancelled || syncVersion !== authSyncVersion) {
                return;
              }

              clearTimeout(loadingFallbackTimer);
              setProfile(profileData);
              setLoadingGlobal(false);

            },
            (error) => {

              if (isCancelled || syncVersion !== authSyncVersion) {
                return;
              }

              clearTimeout(loadingFallbackTimer);
              console.error("Erreur synchro profil:", error);
              setProfile(null);
              setLoadingGlobal(false);
              setAuthError("Ton profil n a pas pu etre charge. Reessaie de te connecter.");

            }
          );

        } else {

          setAuthUser(null);
          setProfile(null);
          setClaims(null);
          setLoadingGlobal(false);

        }

      } catch (error) {

        if (isCancelled || syncVersion !== authSyncVersion) {
          return;
        }

        console.error("Auth sync error:", error);
        setAuthUser(firebaseUser);
        setProfile(null);
        setLoadingGlobal(false);

      }

    });

    return () => {

      isCancelled = true;
      clearTimeout(loadingFallbackTimer);
      profileUnsubscribe();
      unsubscribe();

    };

  }, []);

  useEffect(() => {
    if (!authUser) {
      setRemoteSessionStatus("idle");
      return undefined;
    }

    let isCancelled = false;
    let currentStatus = "idle";

    function updateStatus(next) {
      currentStatus = next;
      if (!isCancelled) setRemoteSessionStatus(next);
    }

    async function tryBootstrap() {
      updateStatus("loading");
      try {
        const result = await retryWithBackoff(
          () => bootstrapRemoteSession(authUser),
          {
            retries: 3,
            baseMs: 1000,
            factor: 2,
            onRetry: () => {
              if (!isCancelled) setRemoteSessionStatus("recovering");
            },
          }
        );

        if (!isCancelled && auth.currentUser) {
          // Force-refresh le token si le backend a mis à jour les custom claims.
          const forceRefresh = result?.claimsUpdated === true;
          const idTokenResult = await auth.currentUser.getIdTokenResult(forceRefresh);
          if (!isCancelled) setClaims(idTokenResult.claims || null);
        }

        updateStatus("ok");
      } catch (error) {
        console.error("[auth] bootstrap session distant echoue:", {
          uid: authUser.uid,
          code: error?.code,
          message: error?.message,
        });
        updateStatus("degraded");
      }
    }

    function handleWindowFocus() {
      if (currentStatus === "degraded" && !isCancelled) {
        tryBootstrap();
      }
    }

    tryBootstrap();
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      isCancelled = true;
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [authUser]);

  const refreshCurrentAuthUser = useCallback(async () => {
    if (!auth.currentUser) {
      return null;
    }

    await reload(auth.currentUser);
    bumpAuthStateVersion();

    return auth.currentUser;
  }, [bumpAuthStateVersion]);

  const syncProfileIdentityFields = useCallback(async () => {}, []);

  useEffect(() => {
    let isCancelled = false;
    const redirectAttempt = consumeRedirectPending();

    getRedirectResult(auth)
      .then((result) => {
        if (isCancelled) {
          return;
        }

        if (!result) {
          return;
        }

        trackConversionEvent("shared_event_auth_completed", {
          provider: result.providerId || result.user?.providerData?.[0]?.providerId || "redirect",
          share_source: readShareAttribution().shareSource || "direct",
        });
        clearAuthError();
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }

        if (isRedirectStorageFailure(error)) {
          enablePopupFallbackForCurrentSession();
          setAuthError(
            redirectAttempt
              ? "Connexion interrompue apres redirection. Réessaie avec Facebook ou utilise la connexion par e-mail."
              : "Ce navigateur bloque la redirection. Réessaie avec Facebook ou utilise la connexion par e-mail."
          );
          return;
        }

        console.error("Redirect auth error:", error);
        setAuthError(normalizeAuthErrorMessage(error));
      });

    return () => {
      isCancelled = true;
    };
  }, [clearAuthError, enablePopupFallbackForCurrentSession]);

  useEffect(() => {
    if (!authUser || !profile) {
      return undefined;
    }

    let isCancelled = false;

    retryWithBackoff(
      () => syncProfileIdentityFields(authUser, profile),
      { retries: 3, baseMs: 300, factor: 2 }
    ).catch((error) => {
      if (!isCancelled) {
        console.error("[auth] sync identite profil echoue apres retries:", {
          uid: authUser.uid,
          code: error?.code,
          message: error?.message,
        });
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [authUser, profile, syncProfileIdentityFields]);


  /* ================================================================
     AUTH ACTION WRAPPER
  ================================================================ */

  /**
   * Centralise :
   * - loader
   * - gestion erreurs
   * - format de retour standard
   */

  const handleAuthAction = useCallback(async (actionFn) => {
    if (authRateLimiter.isBlocked()) {
      const remainingSeconds = Math.ceil(authRateLimiter.getRemainingMs() / 1000);
      const blockedMessage = `Trop de tentatives. Reessaie dans ${remainingSeconds} s.`;
      setAuthError(blockedMessage);
      return { success: false, error: blockedMessage };
    }

    try {
      setLoadingAction(true);
      setAuthError(null);

      const result = await actionFn();

      authRateLimiter.recordSuccess();
      return { success: true, data: result };
    } catch (error) {
      // Fenetre Google/Facebook fermee volontairement : pas une erreur a
      // afficher ni a compter dans la limite de tentatives.
      if (isPopupCancelError(error)) {
        return { success: false, cancelled: true, code: error.code };
      }
      authRateLimiter.recordFailure(error);
      console.error("[auth] action echouee:", error);
      setAuthError(normalizeAuthErrorMessage(error));
      // code : le parcours e-mail unique (QuickAuthPanel) distingue compte
      // inconnu, mot de passe faux et adresse deja utilisee.
      return { success: false, error: normalizeAuthErrorMessage(error), code: error?.code || null };
    } finally {
      setLoadingAction(false);
    }
  }, []);

  /* ================================================================
     AUTH ACTIONS
  ================================================================ */

  const login = useCallback((email, password) =>
    handleAuthAction(() =>
      signInWithEmailAndPassword(auth, email, password)
    ), []);


  const register = useCallback((email, password) =>
    handleAuthAction(async () => {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const verificationEmail = await sendInitialVerificationEmail(result.user, {
        sendEmail: isAuthEmulatorEnabled
          ? sendEmailVerification
          : sendBrandedVerificationEmail,
        isEmulator: isAuthEmulatorEnabled,
      });

      return {
        credential: result,
        user: result.user,
        verificationEmail,
      };
    }), []);

  const resetPassword = useCallback((email) =>
    handleAuthAction(async () => {
      const normalizedEmail = String(email || "").trim();
      if (!normalizedEmail) {
        const error = new Error("Saisis ton adresse e-mail.");
        error.code = "auth/invalid-email";
        throw error;
      }
      // E-mail Karango, repli Firebase si notre serveur ne repond pas
      // (docs/password-reset-email-spec.md).
      await requestPasswordReset(normalizedEmail, {
        sendFirebase: (address) => sendPasswordResetEmail(auth, address),
      });
      return { sent: true };
    }), []);


  /* ================================================================
     GOOGLE LOGIN (SMART)
  ================================================================ */

  /**
   * Connexion Google avec :
   *
   * - sélection forcée du compte
   * - conversion compte anonyme → Google
   */

  const loginWithGoogle = useCallback(() => {
    const provider = new GoogleAuthProvider();

    provider.setCustomParameters({
      prompt: "select_account"
    });

    return handleAuthAction(async () => {
      if (preferredGoogleAuthMode === "redirect") {
        await executeRedirectAuthAction({
          providerId: "google.com",
          operation: auth.currentUser?.isAnonymous ? "link" : "signin",
          run: async () => {
            if (auth.currentUser?.isAnonymous) {
              await linkWithRedirect(auth.currentUser, provider);
            } else {
              await signInWithRedirect(auth, provider);
            }
          },
        });

        return { mode: "redirect" };
      }

      // Retour sur l'app pendant que la fenetre est encore ouverte : on
      // debloque l'interface sans attendre que Firebase le constate.
      const stopWatching = watchReturnFromPopup(() => setLoadingAction(false));
      try {
        if (auth.currentUser?.isAnonymous) {
          const credential = await linkWithPopup(auth.currentUser, provider);
          await reconcileProfileProviderIfNeeded(credential.user);
          return { mode: "popup", credential };
        }

        return {
          mode: "popup",
          credential: await signInWithPopup(auth, provider),
        };
      } finally {
        stopWatching();
      }

    });

  }, [executeRedirectAuthAction, preferredGoogleAuthMode]);


  const loginWithFacebook = useCallback(() => {
    const provider = new FacebookAuthProvider();
    provider.addScope("public_profile");
    provider.addScope("email");

    return handleAuthAction(async () => {
      if (preferredGoogleAuthMode === "redirect") {
        await executeRedirectAuthAction({
          providerId: "facebook.com",
          operation: auth.currentUser?.isAnonymous ? "link" : "signin",
          run: async () => {
            if (auth.currentUser?.isAnonymous) {
              await linkWithRedirect(auth.currentUser, provider);
            } else {
              await signInWithRedirect(auth, provider);
            }
          },
        });

        return { mode: "redirect" };
      }

      // Retour sur l'app pendant que la fenetre est encore ouverte : on
      // debloque l'interface sans attendre que Firebase le constate.
      const stopWatching = watchReturnFromPopup(() => setLoadingAction(false));
      try {
        if (auth.currentUser?.isAnonymous) {
          const credential = await linkWithPopup(auth.currentUser, provider);
          await reconcileProfileProviderIfNeeded(credential.user);
          return { mode: "popup", credential };
        }

        return {
          mode: "popup",
          credential: await signInWithPopup(auth, provider),
        };
      } finally {
        stopWatching();
      }
    });

  }, [executeRedirectAuthAction, preferredGoogleAuthMode]);

  const loginAnonymously = useCallback(() =>
    handleAuthAction(() =>
      signInAnonymously(auth)
    ), []);


  const logout = useCallback(() =>
    handleAuthAction(async () => {
      await unregisterPushDevice();
      return signOut(auth);
    }), []);

  const linkGoogleProvider = useCallback(() => {
    const provider = new GoogleAuthProvider();

    provider.setCustomParameters({
      prompt: "select_account"
    });

    return handleAuthAction(async () => {
      if (!auth.currentUser) {
        throw new Error("Aucun utilisateur connecte.");
      }

      if (resolveLinkedProviderIds(auth.currentUser).includes("google.com")) {
        return { mode: "already-linked" };
      }

      if (preferredGoogleAuthMode === "redirect") {
        await executeRedirectAuthAction({
          providerId: "google.com",
          operation: "link",
          run: async () => {
            await linkWithRedirect(auth.currentUser, provider);
          },
        });
        return { mode: "redirect" };
      }

      const credential = await linkWithPopup(auth.currentUser, provider);
      await reconcileProfileProviderIfNeeded(credential.user);
      await refreshCurrentAuthUser();

      return {
        mode: "popup",
        credential,
      };
    });
  }, [
    executeRedirectAuthAction,
    preferredGoogleAuthMode,
    refreshCurrentAuthUser,
  ]);

  const linkFacebookProvider = useCallback(() => {
    const provider = new FacebookAuthProvider();

    return handleAuthAction(async () => {
      if (!auth.currentUser) {
        throw new Error("Aucun utilisateur connecte.");
      }

      if (resolveLinkedProviderIds(auth.currentUser).includes("facebook.com")) {
        return { mode: "already-linked" };
      }

      if (preferredGoogleAuthMode === "redirect") {
        await executeRedirectAuthAction({
          providerId: "facebook.com",
          operation: "link",
          run: async () => {
            await linkWithRedirect(auth.currentUser, provider);
          },
        });
        return { mode: "redirect" };
      }

      const credential = await linkWithPopup(auth.currentUser, provider);
      await reconcileProfileProviderIfNeeded(credential.user);
      await refreshCurrentAuthUser();

      return {
        mode: "popup",
        credential,
      };
    });
  }, [
    executeRedirectAuthAction,
    preferredGoogleAuthMode,
    refreshCurrentAuthUser,
  ]);

  const linkEmailPassword = useCallback((email, password) =>
    handleAuthAction(async () => {
      if (!auth.currentUser) {
        throw new Error("Aucun utilisateur connecte.");
      }

      if (resolveLinkedProviderIds(auth.currentUser).includes("password")) {
        return { mode: "already-linked" };
      }

      const normalizedEmail = String(email || "").trim();
      const normalizedPassword = String(password || "");

      if (!normalizedEmail) {
        const error = new Error("L adresse email est requise.");
        error.code = "auth/invalid-email";
        throw error;
      }

      if (!normalizedPassword) {
        const error = new Error("Le mot de passe est requis.");
        error.code = "auth/missing-password";
        throw error;
      }

      const credential = EmailAuthProvider.credential(normalizedEmail, normalizedPassword);
      const result = await linkWithCredential(auth.currentUser, credential);

      await refreshCurrentAuthUser();

      return result;
    }), [refreshCurrentAuthUser]);

  const sendVerificationEmailAction = useCallback(() =>
    handleAuthAction(async () => {
      if (!auth.currentUser?.email) {
        throw new Error("Ajoute d abord une adresse email a ton compte.");
      }

      if (isAuthEmulatorEnabled) {
        await sendEmailVerification(auth.currentUser);
      } else {
        await sendBrandedVerificationEmail(auth.currentUser);
      }

      return { sent: true };
    }), []);

  const deleteAccount = useCallback(() =>
    handleAuthAction(async () => {
      if (!auth.currentUser) {
        throw new Error("Aucun utilisateur connecte.");
      }

      await unregisterPushDevice();
      await deleteFirebaseUser(auth.currentUser);
      return { deleted: true };
    }), []);


  /* ================================================================
     USER MERGE
  ================================================================ */

  /**
   * Fusion des données Auth + Profile
   */

  const user = useMemo(() => {

    const currentAuthUser = auth.currentUser || authUser;

    if (!currentAuthUser) return null;

    return {

      ...currentAuthUser,
      ...profile,
      email: currentAuthUser.email || profile?.email || null,
      emailVerified: Boolean(currentAuthUser.emailVerified),
      isAnonymous: Boolean(currentAuthUser.isAnonymous),
      provider: resolvePrimaryProviderId(currentAuthUser),
      providerData: currentAuthUser.providerData || [],
      photoURL: resolveProfilePhotoUrl(profile, currentAuthUser),
      claims: claims || null,

    };

  }, [authStateVersion, authUser, profile, claims]);

  const linkedProviders = useMemo(
    () => resolveLinkedProviderIds(auth.currentUser || authUser),
    [authStateVersion, authUser]
  );


  /* ================================================================
     CONTEXT VALUE
  ================================================================ */

  const value = useMemo(() => ({

    user,
    authUser: auth.currentUser || authUser,
    profile,
    linkedProviders,
    emailVerified: Boolean((auth.currentUser || authUser)?.emailVerified),

    loading: loadingGlobal,
    loadingAction,
    authError,
    remoteSessionStatus,

    isAuthenticated: !!authUser,
    isAnonymous: authUser?.isAnonymous ?? false,

    userId: authUser?.uid ?? null,
    role: claims?.role ?? profile?.role ?? null,

    login,
    register,
    resetPassword,
    loginWithGoogle,
    loginWithFacebook,
    loginAnonymously,
    logout,
    linkGoogleProvider,
    linkFacebookProvider,
    linkEmailPassword,
    sendVerificationEmail: sendVerificationEmailAction,
    deleteAccount,

    refreshProfile,
    refreshCurrentAuthUser,
    clearAuthError,
    preferredGoogleAuthMode

  }), [
    user,
    authUser,
    profile,
    linkedProviders,
    loadingGlobal,
    loadingAction,
    authError,
    remoteSessionStatus,
    login,
    register,
    resetPassword,
    loginWithGoogle,
    loginWithFacebook,
    loginAnonymously,
    logout,
    linkGoogleProvider,
    linkFacebookProvider,
    linkEmailPassword,
    sendVerificationEmailAction,
    deleteAccount,
    refreshProfile,
    refreshCurrentAuthUser,
    clearAuthError,
    preferredGoogleAuthMode
  ]);


  return (

    <AuthContext.Provider value={value}>

      {children}

    </AuthContext.Provider>

  );

}


/* =====================================================================
   CUSTOM HOOK
===================================================================== */

export function useAuth() {

  const context = useContext(AuthContext);

  if (!context) {

    throw new Error(
      "useAuth doit être utilisé dans AuthProvider"
    );

  }

  return context;

}
